/* eslint-disable */
// ContactForm.js — CONTACT-004
// Public sales enquiry form. Route: /contact
// Captures Agency leads so you have context before a sales call.
//
// PRICE-005: gated on LET UNITS now, not property count. Property count
// stopped being a reliable proxy for "should this person talk to sales"
// the moment pricing moved to units — a landlord with 60 single-let flats
// (60 units) is comfortably self-serve on Pro, while a landlord with 15
// six-bed HMOs (90 rooms) may already be past Agency's 150-unit threshold
// despite a much smaller property count. The dropdown below asks about the
// number that actually decides the answer.
//
// Added an optional "how many properties is that across" field for sales
// context beyond the strict fix — flag if you'd rather it wasn't there.
// It's sent to the backend alongside everything else; worth confirming
// /api/contact doesn't drop unrecognised fields before relying on it
// showing up wherever these enquiries land.

import { API_URL } from '../../config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const UNIT_COUNTS = [
  '150–249 let units',
  '250–499 let units',
  '500–999 let units',
  '1,000+ let units',
];

export default function ContactForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    unitCount: '', propertyCount: '', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim())  return setError('Please enter your name.');
    if (!form.email.trim()) return setError('Please enter your email address.');

    setLoading(true);
    try {
      const BASE = API_URL;
      const res  = await fetch(`${BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, enquiryType: 'agency' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send your enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.7rem 0.85rem', borderRadius: '0.5rem',
    border: '1px solid #d1d5db', fontSize: '0.9375rem', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.8125rem', fontWeight: 600,
    color: '#374151', marginBottom: '0.35rem',
  };

  // ── Success state ────────────────────────────────────────────────
  if (sent) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: '1rem', padding: '3rem 2.5rem', maxWidth: 460, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#166534', marginBottom: '0.5rem' }}>Thanks, {form.name.split(' ')[0]}!</h1>
          <p style={{ color: '#6b7280', marginBottom: '2rem', lineHeight: 1.6 }}>
            We've got your enquiry and we'll be in touch within one working day to talk through what you need.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/pricing')} style={{ background: '#166534', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.7rem 1.25rem', fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer' }}>
              Back to Pricing
            </button>
            <button onClick={() => navigate('/login')} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '0.5rem', padding: '0.7rem 1.25rem', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer' }}>
              Start Free Instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <img src="/casavello-icon-500.png" alt="" style={{ height: 30, width: 30, objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/pricing')} />
        <span style={{ fontWeight: 800, fontSize: '1.2rem', color: '#166534', cursor: 'pointer' }} onClick={() => navigate('/pricing')}>casavello</span>
      </div>

      <div style={{ maxWidth: 620, margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#111', marginBottom: '0.6rem' }}>
            Let's talk about your portfolio
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>
            Managing more than 150 let units, or running a letting agency? Tell us a bit about your
            setup and we'll put together a plan that fits.
          </p>
        </div>

        <div style={{ background: '#fff', borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.7rem 0.9rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Your name *</label>
                <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" required autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Email address *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@youragency.co.uk" required />
              </div>
              <div>
                <label style={labelStyle}>Phone number</label>
                <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 900000" />
              </div>
              <div>
                <label style={labelStyle}>Company</label>
                <input style={inputStyle} value={form.company} onChange={e => set('company', e.target.value)} placeholder="Your Agency Ltd" />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>How many let units do you manage?</label>
              <select style={inputStyle} value={form.unitCount} onChange={e => set('unitCount', e.target.value)}>
                <option value="">Select a range…</option>
                {UNIT_COUNTS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>
                A single let counts as 1 unit; a six-bed HMO counts as 6. A rough estimate is fine.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Roughly how many properties is that across? <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <input style={inputStyle} value={form.propertyCount} onChange={e => set('propertyCount', e.target.value)} placeholder="e.g. 15 HMOs, or 40 single lets" />
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>
                Helps us understand your portfolio before we call — HMO-heavy and single-let portfolios need different things from us.
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Anything else we should know?</label>
              <textarea
                style={{ ...inputStyle, minHeight: 110, resize: 'vertical' }}
                value={form.message}
                onChange={e => set('message', e.target.value)}
                placeholder="What are you using at the moment? Any particular problems you're trying to solve?"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', background: '#166534', color: '#fff', border: 'none',
                borderRadius: '0.5rem', padding: '0.85rem', fontWeight: 700,
                fontSize: '0.9375rem', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Sending…' : 'Send Enquiry'}
            </button>

            <p style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', margin: '0.9rem 0 0' }}>
              We'll only use these details to respond to your enquiry.
            </p>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
            Under 150 let units? Our{' '}
            <span onClick={() => navigate('/pricing')} style={{ color: '#166534', cursor: 'pointer', textDecoration: 'underline' }}>
              self-serve plans
            </span>{' '}
            start at £8.00/month and you can be up and running in minutes.
          </p>
          <button onClick={() => navigate('/pricing')} style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline' }}>
            ← Back to pricing
          </button>
        </div>
      </div>
    </div>
  );
}
